import { useState, useEffect } from 'react';

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  screenWidth: number;
  screenHeight: number;
}

/**
 * Hook para detectar el tipo de dispositivo usando User-Agent y dimensiones de pantalla
 * @returns DeviceInfo con información del dispositivo
 */
export const useDeviceDetection = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() =>
    getDeviceInfo()
  );

  useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(getDeviceInfo());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return deviceInfo;
};

/**
 * Obtiene información del dispositivo actual
 */
function getDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  // Detectar móviles mediante User-Agent
  const mobileRegex = /android|webos|iphone|ipod|blackberry|iemobile|opera mini|mobile/i;
  const tabletRegex = /ipad|android(?!.*mobile)|tablet|kindle|silk|playbook/i;

  const isMobileUA = mobileRegex.test(ua);
  const isTabletUA = tabletRegex.test(ua);

  // Detectar también por tamaño de pantalla como fallback
  const isMobileScreen = screenWidth < 768;
  const isTabletScreen = screenWidth >= 768 && screenWidth < 1024;

  // Combinar detección de UA y pantalla
  const isMobile = isMobileUA || (isMobileScreen && !isTabletUA);
  const isTablet = isTabletUA || (isTabletScreen && !isMobileUA);
  const isDesktop = !isMobile && !isTablet;

  // Determinar tipo de dispositivo
  let deviceType: 'mobile' | 'tablet' | 'desktop';
  if (isMobile) {
    deviceType = 'mobile';
  } else if (isTablet) {
    deviceType = 'tablet';
  } else {
    deviceType = 'desktop';
  }

  return {
    isMobile,
    isTablet,
    isDesktop,
    deviceType,
    screenWidth,
    screenHeight,
  };
}

/**
 * Función auxiliar para usar fuera de componentes React
 */
export const detectDevice = (): DeviceInfo => {
  return getDeviceInfo();
};
